import React, { useContext, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useAggregate } from 'hooks';
import { Context as AuthContext } from 'contexts/auth';
import { Context as AlertContext } from 'contexts/alert';
import { Config } from 'configs';
import { files } from 'codemash';
import { apiActionChooser } from 'utils/apiActionChooser';
import { getFileUrlByFileId } from 'api/client/requests';
import { currentUtcMillis, getFullDateInCustomTimezone } from 'utils/dateManagement';
import { DataColumn, LinkColumn, List, ListHeader } from 'components/lists';
import { joinClassNames } from 'utils/cssHelper';
import { PageTitle } from 'components/layouts/pageTitle';
import { ConfirmationDialog, ConfirmationDialogTypes } from 'components/lists/confirmationDialog';
import { payRKeeperOrder, updateOrderFields } from 'api/orders';
import { ListLayout } from 'components/layouts/listLayout';
import { DropdownWrapper } from 'components/formInputs/wrappers/dropdownWrapper';
import { Loader } from 'components/indicators/loader';
import { TextboxWrapper } from 'components/formInputs/wrappers/textboxWrapper';
import { FormButton } from 'components/buttons/formButton';
import { roundDecimal } from 'utils/roundingHelpers';
import { getProgressStatusOptions } from './enums';
import { getDeliveryTimeText } from './helpers';
import { OrderRefundRequestAddForm } from './partials/_popup_refund_request';

export const OrderDetails = () => {
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const params = useParams();
  const { id } = params;

  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [isCompletingOrder, setIsCompletingOrder] = useState(false);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [isLoadingDueTime, setIsLoadingDueTime] = useState(false);
  const [selectedDueTimeChange, setSelectedDueTimeChange] = useState(null);
  const [dueTimeChangeConfirmVisible, setDueTimeChangeConfirmVisible] = useState(false);

  const [editingOrderLines, setEditingOrderLines] = useState({});
  const [editingOrderLinesValues, setEditingOrderLinesValues] = useState({});
  const [isLoadingOrderLine, setisLoadingOrderLine] = useState({});

  const requestData = {
    collectionName: Config.DATA.COLLECTIONS.ORDERS,
    aggregateId: Config.DATA.AGGREGATES.GET_ORDER_BY_ID,
    secretKey: auth.token,
    filter: { company: auth.company?.company, orderId: id },
    aggregateIdV2: 'AdminGetOrderDetails',
    companyIdForV2: auth.company?.company,
  };

  const [findOrders, orders, isLoading, , , isRefreshing] = useAggregate(requestData);

  const order = orders && orders.length > 0 ? orders[0] : null;

  const downloadInvoice = async (e, fileInfo) => {
    e.preventDefault();

    try {
      let urlResponse;

      await apiActionChooser(auth.company?.company, async () => {
        urlResponse = await files.getFileUrl({
          secretKey: auth.token,
          fileId: fileInfo.id,
        });
      }, async () => {
        urlResponse = await getFileUrlByFileId({
          fileId: fileInfo.id,
        });
      });

      if (urlResponse) {
        const urlToFile = decodeURI(urlResponse.result);

        window.open(urlToFile, '_blank');
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getTranslatedData = (translations) => {
    if (!translations) {
      return '';
    }

    if (Object.keys(translations).length > 0) {
      // eslint-disable-next-line no-restricted-syntax, guard-for-in, no-unreachable-loop
      for (const key in translations) {
        return translations[key];
      }
    }

    return '';
  };

  const onProgressStatusChange = async (val) => {
    setIsLoadingProgress(true);

    const response = await updateOrderFields({
      token: auth.token,
      orderId: order._id,
      fields: ['progress_status'],
      values: [val],
      alertController,
      companyId: auth.company?.company,
    });

    setIsLoadingProgress(false);

    if (!response) {
      return;
    }

    if (response.ordersUpdateResponse && response.ordersUpdateResponse.matchedCount > 0) {
      alertController.success({ text: 'Order progress updated' });
    }

    if (response.needRefreshOrder) {
      findOrders(true);
    }
  };

  const onOrderLineUpdate = async (ind) => {
    setisLoadingOrderLine((prev) => ({ ...prev, [ind]: true }));

    const submitData = editingOrderLinesValues[ind];
    const response = await updateOrderFields({
      token: auth.token,
      orderId: order._id,
      fields: [`order_line_quantity.${ind}`],
      values: [submitData.quantity],
      alertController,
      companyId: auth.company?.company,
    });

    setisLoadingOrderLine((prev) => ({ ...prev, [ind]: false }));

    if (!response) {
      return;
    }

    if (
      response.syncedOrdersUpdateResponse &&
      response.syncedOrdersUpdateResponse.matchedCount > 0
    ) {
      alertController.success({ text: 'Order line updated' });
    }

    if (response.needRefreshOrder) {
      findOrders(true);
    }
  };

  const onDueTimeChange = async (minutes) => {
    setIsLoadingDueTime(true);

    const response = await updateOrderFields({
      token: auth.token,
      orderId: order._id,
      fields: ['dalivery_date'],
      values: [minutes],
      alertController,
      companyId: auth.company?.company,
    });

    if (!response) {
      setIsLoadingDueTime(false);
      return;
    }

    if (response.ordersUpdateResponse && response.ordersUpdateResponse.matchedCount > 0) {
      // eslint-disable-next-line no-param-reassign
      orders[0].delivery_date =
        (order.delivery_date || currentUtcMillis()) + (parseInt(minutes, 10) || 0) * 60000;

      if (order.prepare_date && order.prepare_date > 0) {
        // eslint-disable-next-line no-param-reassign
        orders[0].prepare_date = order.prepare_date + (parseInt(minutes, 10) || 0) * 60000;
      }

      alertController.success({ text: 'Delivery date updated' });
    }

    setIsLoadingDueTime(false);
  };

  // Not show this for now
  const canCompleteOrder =
    false &&
    order &&
    (order.external_provider === 'rkeeper' || order.external_provider === 'iiko') &&
    order.status === 'New';

  const isProviderOrder =
    order && (order.external_provider === 'rkeeper' || order.external_provider === 'iiko');
  const isDeliveryOrder = order && order.is_delivery;
  const isTakeawayOrder = order && order.is_takeout;

  const onCompleteOrder = async () => {
    setIsCompletingOrder(true);

    const response = await payRKeeperOrder({
      token: auth.token,
      orderId: order._id,
      alertController,
    });

    if (response && (response.paid || response.alreadyPaid)) {
      findOrders(true);
    }

    setIsCompletingOrder(false);
  };

  const renderTableName = () => {
    let tableFirstPart = '';

    const tableName = order?.table?.number || '';
    const tableType = order?.table?.type || '';

    if (tableType === 'Take Away') {
      tableFirstPart = 'Takeaway:';
    } else {
      tableFirstPart = 'Table:';
    }

    return `${tableFirstPart} ${tableName || ''}`.trim();
  };

  const renderDeliveryAddress = () => {
    if (!order || !isDeliveryOrder) {
      return null;
    }

    const parts = [];

    if (order.delivery_address) {
      parts.push(order.delivery_address);
    }

    if (order.delivery_city) {
      parts.push(order.delivery_city);
    }

    if (order.delivery_zip) {
      parts.push(order.delivery_zip);
    }

    return parts.join(', ');
  };

  /*
  const renderTips = () => {
    let total = 0;

    if (order.payments) {
      order.payments.forEach((payment) => {
        total += (payment.tips || 0);
      });
    }

    return total.toFixed(2);
  };

  const renderPaid = () => {
    let total = 0;

    if (order.payments) {
      order.payments.forEach((payment) => {
        total += (payment.amount || 0);
      });
    }

    return total.toFixed(2);
  }; */

  const totalFullOrderPaid = useMemo(() => {
    let paidAmount = 0;

    if (order && order.payments) {
      order.payments.forEach((payment) => {
        paidAmount = roundDecimal(
          paidAmount +
            (payment.amount || 0) -
            (payment.discount || 0) +
            (payment.packaging_price || 0) +
            (payment.delivery_price || 0) +
            (payment.reach_min_price || 0),
        );
      });
    }

    return roundDecimal(paidAmount);
  }, [order]);

  const totalFullTipsPaid = useMemo(() => {
    let paidAmount = 0;

    if (order && order.payments) {
      order.payments.forEach((payment) => {
        paidAmount = roundDecimal(paidAmount + (payment.tips || 0));
      });
    }

    return roundDecimal(paidAmount);
  }, [order]);

  const { totalFullOrderRefundRequested, totalFullTipsRefundRequested } = useMemo(() => {
    let amount = 0;
    let tipsAmount = 0;

    if (order && order.refunds) {
      order.refunds.forEach((refund) => {
        amount = roundDecimal(amount + (refund.refund_amount || 0));

        tipsAmount = roundDecimal(tipsAmount + (refund.refund_tips_amount || 0));
      });
    }

    return {
      totalFullOrderRefundRequested: roundDecimal(amount),
      totalFullTipsRefundRequested: roundDecimal(tipsAmount),
    };
  }, [order]);

  const kitchenNote =
    order && order.payments && order.payments.length > 0 ? order.payments[0].kitchen_note : null;
  const deliveryNote = order ? order.note_for_driver : null;

  const isWoltDeliveryOrder = order && order.delivery_venue && order.delivery_venue.provider === 'Wolt';

  const renderAddDeliveryMinutesButton = (minutes) => {
    return (
      <button
        type="button"
        onClick={() => {
          setDueTimeChangeConfirmVisible(true);
          setSelectedDueTimeChange(minutes);
        }}
        className="bg-white py-1 px-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 inline-flex justify-center
                    items-center hover:bg-gray-50 disabled:opacity-50 mr-2 mt-2"
        disabled={isLoadingDueTime}
      >
        + {minutes} min
        {isLoadingDueTime && selectedDueTimeChange === minutes && (
          <div className="inline-block ml-2">
            <Loader xxs />
          </div>
        )}
      </button>
    );
  };

  const renderAdditionalDetails = () => {
    const valuesToRender = [];

    if (order?.room_number) {
      valuesToRender.push({ label: 'Room Number', value: order.room_number });
    }

    if (valuesToRender.length === 0) {
      return null;
    }

    return (
      <div className="w-full mt-10">
        <ListHeader title="Additional Details" description="Additional client provided information" />

        <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-8">
          <div className="border-t border-gray-200">
            <dl>
              {valuesToRender.map((item, ind) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={ind} className={`${ind % 2 === 1 ? 'bg-gray-50' : 'bg-white'}  px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6`}>
                    <dt className="text-sm font-medium text-gray-500">{item.label}</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {item.value || '-'}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        </div>
      </div>
    );
  };

  const providerOrderNumber = order && order.syncedOrder && order.syncedOrder.rkeeper_order_number;

  return (
    <ListLayout isLoading={isLoading && !isRefreshing}>
      {!!order && (
        <>
          <PageTitle
            renderTitle={() => (
              <>
                <span>
                  Order #{order.number}
                  {providerOrderNumber ? (
                    <span className="text-2xl text-gray-400 font-medium">
                      {' '}
                      (#{providerOrderNumber})
                    </span>
                  ) : (
                    ''
                  )}{' '}
                </span>
                <span
                  className={joinClassNames(
                    'text-2xl font-bold',
                    order.status === 'Completed' ? 'text-green-700' : 'text-red-700',
                  )}
                >
                  ({order.status})
                </span>
              </>
            )}
            breadcrumbs={[{ path: '/orders', title: 'Orders' }]}
            alignLeft
          />
          <div className="my-10 max-w-7xl mx-auto px-2 md:px-6 lg:px-8">
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 sm:px-6 py-5 grid grid-cols-3 gap-4">
                <div className={canCompleteOrder ? 'col-span-3 sm:col-span-2' : 'col-span-3'}>
                  <p className="mt-1 max-w-2xl text-sm text-gray-500">
                    Restaurant: {order.restaurant?.name || ''}
                  </p>
                  {!!order.syncedOrder?.waiter_name && (
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">
                      Served by: {order.syncedOrder.waiter_name}
                    </p>
                  )}
                  {!!order.table && (
                    <p className="mt-1 max-w-2xl text-sm text-gray-500">{renderTableName()}</p>
                  )}
                </div>
                {canCompleteOrder && (
                  <div className="col-span-3 sm:col-span-1 sm:text-right">
                    <button
                      type="button"
                      className={`${
                        isCompletingOrder ? 'disabled:opacity-50' : ''
                      } sm:ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                      onClick={() => {
                        setShowCompleteDialog(true);
                      }}
                      disabled={isCompletingOrder}
                    >
                      Complete
                    </button>
                  </div>
                )}
              </div>
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Placed On</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {getFullDateInCustomTimezone(order.date_utc, order.restaurant.zone)}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {isTakeawayOrder &&
                        (order.delivery_date
                          ? getDeliveryTimeText({ order, timezone: order.restaurant.zone })
                          : '-')}
                      {isDeliveryOrder && (
                        <>
                          <div>
                            <span className="font-semibold">Prepare: </span>
                            {order.delivery_date
                              ? getDeliveryTimeText({
                                order,
                                timezone: order.restaurant.zone,
                                isPrepare: true,
                              })
                              : '-'}
                          </div>
                          <div className="mt-1 mb-1">
                            <span className="font-semibold">Deliver: </span>
                            {order.delivery_date
                              ? getDeliveryTimeText({
                                order,
                                timezone: order.restaurant.zone,
                                isPrepare: false,
                              })
                              : '-'}
                          </div>
                        </>
                      )}
                      {!isTakeawayOrder && !isDeliveryOrder && <span>Now</span>}
                      {order.delivery_date &&
                      !(order.delivery_venue && order.delivery_venue.provider === 'Wolt') ? (
                        <div className="">
                          {renderAddDeliveryMinutesButton(5)}
                          {renderAddDeliveryMinutesButton(10)}
                          {renderAddDeliveryMinutesButton(15)}
                          {renderAddDeliveryMinutesButton(25)}
                          {renderAddDeliveryMinutesButton(30)}
                        </div>
                        ) : (
                          ''
                        )}
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Kitechen Note</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {kitchenNote || '-'}
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 items-center">
                    <dt className="text-sm font-medium text-gray-500">Progress</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 col-span-2 lg:col-span-1">
                      <div className="flex flex-row items-center">
                        <div className="flex-1">
                          <DropdownWrapper
                            name="progress_status"
                            options={getProgressStatusOptions({
                              isDeliveryOrder,
                              isTakeawayOrder,
                              isProviderOrder,
                            })}
                            responsiveClassName="col-span-6 lg:col-span-4"
                            onChange={(e) => {
                              onProgressStatusChange(e.target.value);
                            }}
                            defaultValue={order.progress_status}
                            disabled={isLoadingProgress}
                          />
                        </div>
                        {isLoadingProgress && (
                          <div className="ml-2">
                            <Loader smallest />
                          </div>
                        )}
                      </div>
                    </dd>
                  </div>
                  {/* <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500 sm:flex sm:items-center">Refund Request</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <FormButton
                      // disabled={updatingSettings}
                      // isLoading={updatingSettings}
                      onClick={() => {
                        setShowRefundDialog(true);
                      }}
                      text="Full / Partial Refund"
                      noMargin
                    />
                  </dd>
                </div> */}
                </dl>
              </div>
            </div>

            {isDeliveryOrder && (
              <div className="w-full mt-10">
                <ListHeader title="Delivery Details" description="Delivery information" />
                {isWoltDeliveryOrder && (
                <p className="mt-2 text-sm text-gray-700">
                  If you have questions about delivery, call
                  <a href="tel:+37052140505" className="text-indigo-600 hover:text-indigo-900">+37052140505</a> and provide the name of the location.
                </p>
                )}

                <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-8">
                  <div className="border-t border-gray-200">
                    <dl>
                      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Delivery Note</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {deliveryNote || '-'}
                        </dd>
                      </div>
                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Address</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {renderDeliveryAddress() || '-'}
                        </dd>
                      </div>
                      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Client Name</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {order.delivery_name || '-'}
                        </dd>
                      </div>
                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Client Phone</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {order.delivery_phone ? (
                            <a
                              className="underline text-blue-500"
                              href={`tel:${order.delivery_phone || ''}`}
                            >
                              {order.delivery_phone || ''}
                            </a>
                          ) : '-'}
                        </dd>
                      </div>
                      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Client Email</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {order.delivery_email ? (
                            <a
                              className="underline text-blue-500"
                              href={`mailto:${order.delivery_email || ''}`}
                            >
                              {order.delivery_email || ''}
                            </a>
                          ) : '-'}
                        </dd>
                      </div>

                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">External Order ID</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {order.delivery_order ? order.delivery_order.external_order_id || '-' : '-'}
                        </dd>
                      </div>
                      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">External Delivery ID</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {order.delivery_order ? order.delivery_order.external_delivery_id || '-' : '-'}
                        </dd>
                      </div>
                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Tracking URL</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {!!order.delivery_order && !!order.delivery_order.tracking_url ?
                            <a target="_blank" className="text-indigo-600 hover:text-indigo-900" href={order.delivery_order.tracking_url} rel="noreferrer">Open</a> : '-'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            )}

            {isTakeawayOrder && (
              <div className="w-full mt-10">
                <ListHeader title="Client Details" description="Client information" />

                <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-8">
                  <div className="border-t border-gray-200">
                    <dl>
                      <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Client Phone</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {order.delivery_phone ? (
                            <a
                              className="underline text-blue-500"
                              href={`tel:${order.delivery_phone || ''}`}
                            >
                              {order.delivery_phone || ''}
                            </a>
                          ) : '-'}
                        </dd>
                      </div>

                      <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                        <dt className="text-sm font-medium text-gray-500">Client Email</dt>
                        <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                          {order.delivery_email ? (
                            <a
                              className="underline text-blue-500"
                              href={`mailto:${order.delivery_email || ''}`}
                            >
                              {order.delivery_email || ''}
                            </a>
                          ) : '-'}
                        </dd>
                      </div>
                    </dl>
                  </div>
                </div>
              </div>
            )}

            {renderAdditionalDetails()}

            <div className="w-full mt-10">
              <ListHeader title="Order Lines" description="Products in the order" />
              <List
                source={order.syncedOrder?.products || []}
                keyField={isProviderOrder ? 'rkeeper_dish_code' : '_index'}
              >
                <DataColumn
                  name="Product name"
                  dbColumnName="product_name"
                  render={(x) => {
                    let variationsToShow = x.variations;

                    if (!variationsToShow && x.variations_json) {
                      variationsToShow = JSON.parse(x.variations_json);
                    }

                    return (
                      <div>
                        {getTranslatedData(x.product_name)}
                        {!!variationsToShow &&
                          variationsToShow.length > 0 &&
                          variationsToShow.map((variation) => {
                            return (
                              <div key={variation.id}>
                                {!!variation.items &&
                                  variation.items.length > 0 &&
                                  variation.items.map((item) => {
                                    return (
                                      <div key={item.id} className="text-gray-400 text-sm mt-1">
                                        - {getTranslatedData(item.name)}
                                      </div>
                                    );
                                  })}
                              </div>
                            );
                          })}
                      </div>
                    );
                  }}
                  headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Amount"
                  dbColumnName="quantity"
                  render={(x, ind) => {
                    if (isProviderOrder) {
                      return x.quantity;
                    }

                    if (!editingOrderLines[ind]) {
                      return x.quantity;
                    }

                    return (
                      <div className="max-w-[100px] inline-block">
                        <TextboxWrapper
                          value={editingOrderLinesValues[ind].quantity}
                          onChange={(e) => {
                            setEditingOrderLinesValues((prev) => {
                              const newVal = { ...prev };

                              newVal[ind].quantity =
                                (parseInt(e.target.value, 10) || 0) < 0
                                  ? 0
                                  : parseInt(e.target.value, 10);
                              return newVal;
                            });
                          }}
                        />
                      </div>
                    );
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Total"
                  dbColumnName="total"
                  render={(x) => {
                    return `${(x.full_price || 0).toFixed(2)} Eur`;
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
                {!isProviderOrder && (
                  <LinkColumn
                    // eslint-disable-next-line react/no-unstable-nested-components
                    titleFunc={(ind) => {
                      if (isLoadingOrderLine[ind]) {
                        return <Loader smallest />;
                      }

                      if (editingOrderLines[ind]) {
                        return 'Save';
                      }

                      return 'Edit';
                    }}
                    onClick={(x, ind) => {
                      if (editingOrderLines[ind]) {
                        onOrderLineUpdate(ind);
                        // eslint-disable-next-line no-param-reassign
                        x.quantity = editingOrderLinesValues[ind].quantity;
                      } else {
                        setEditingOrderLinesValues((prev) => ({
                          ...prev,
                          [ind]: { quantity: x.quantity },
                        }));
                      }

                      setEditingOrderLines((prev) => ({ ...prev, [ind]: !prev[ind] }));
                    }}
                    className="relative py-3.5 pl-3 sm:pr-6"
                    dataCellClass="whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6"
                  />
                )}
              </List>
            </div>

            <div className="w-full mt-10">
              <ListHeader title="Payments" description="Payments made for the order">
                <FormButton
                  onClick={() => {
                    setShowRefundDialog(true);
                  }}
                  text="Full / Partial Refund"
                  noMargin
                />
              </ListHeader>

              {/* <div className="bg-white shadow overflow-hidden sm:rounded-lg mt-8">
              <div className="border-t border-gray-200">
                <dl>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Cart Total</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {(order.syncedOrder?.order_sum || 0).toFixed(2)} Eur
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Discount</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {(order.syncedOrder?.discount || 0).toFixed(2)} Eur
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Surcharge</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {(order.syncedOrder?.surcharge || 0).toFixed(2)} Eur
                    </dd>
                  </div>
                  <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Tips</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {renderTips()} Eur
                    </dd>
                  </div>
                  <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Total Paid</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {renderPaid()} Eur
                    </dd>
                  </div>
                </dl>
              </div>
            </div> */}

              <List source={order.payments || []} keyField="_id">
                <DataColumn
                  name="Date"
                  dbColumnName="paid_on"
                  render={(x) => getFullDateInCustomTimezone(x.paid_on, order?.restaurant?.zone)}
                  headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Total"
                  dbColumnName="total"
                  render={(x) => {
                    return `${roundDecimal(
                      (x.amount || 0) +
                        (x.tips || 0) -
                        (x.discount || 0) +
                        (x.packaging_price || 0) +
                        (x.delivery_price || 0) +
                        (x.reach_min_price || 0),
                    ).toFixed(2)} Eur`;
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Cart"
                  dbColumnName="amount"
                  render={(x) => {
                    return `${(x.amount || 0).toFixed(2)} Eur`;
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Tips"
                  dbColumnName="tips"
                  render={(x) => {
                    return `${(x.tips || 0).toFixed(2)} Eur`;
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Discount"
                  dbColumnName="discount"
                  render={(x) => {
                    return `${(Math.abs(x.discount || 0) * -1).toFixed(2)} Eur`;
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Packaging"
                  dbColumnName="packaging_price"
                  render={(x) => {
                    return `${(x.packaging_price || 0).toFixed(2)} Eur`;
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Delivery"
                  dbColumnName="delivery_price"
                  render={(x) => {
                    return `${(x.delivery_price || 0).toFixed(2)} Eur`;
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Reach minimum"
                  dbColumnName="reach_min_price"
                  render={(x) => {
                    return `${(x.reach_min_price || 0).toFixed(2)} Eur`;
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
                <DataColumn
                  name="Invoice"
                  dbColumnName="invoice"
                  render={(x) => {
                    if (!x.invoice || x.invoice.length === 0) {
                      return null;
                    }

                    const invoice = x.invoice[0];

                    return (
                      <div className="ml-4 flex-shrink-1">
                        <a
                          href="/"
                          onClick={(e) => downloadInvoice(e, invoice)}
                          className="font-medium text-indigo-600 hover:text-indigo-500"
                        >
                          Download
                        </a>
                      </div>
                    );
                  }}
                  headerClass="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                  dataCellClass="px-6 py-4 text-right whitespace-nowrap text-sm font-medium text-gray-900"
                />
              </List>
            </div>

            {!!order?.refunds && order.refunds.length > 0 && (
              <div className="w-full mt-10">
                <ListHeader title="Refunds" description="All requested refunds" />

                <List source={order.refunds || []} keyField="_id">
                  <DataColumn
                    name="Date"
                    dbColumnName="request_date"
                    render={(x) =>
                      getFullDateInCustomTimezone(x.request_date, order?.restaurant?.zone)}
                    headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    dataCellClass="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  />
                  <DataColumn
                    name="Order Amount"
                    dbColumnName="refund_amount"
                    render={(x) => {
                      return `${(x.refund_amount || 0).toFixed(2)} Eur`;
                    }}
                    headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    dataCellClass="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  />
                  <DataColumn
                    name="Tips Amount"
                    dbColumnName="refund_tips_amount"
                    render={(x) => {
                      return `${(x.refund_tips_amount || 0).toFixed(2)} Eur`;
                    }}
                    headerClass="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    dataCellClass="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900"
                  />
                  <DataColumn
                    name="Processed"
                    dbColumnName="processed"
                    headerClass="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                    dataCellClass="whitespace-nowrap px-3 py-4 text-sm text-gray-500 sm:table-cell"
                  />
                </List>
              </div>
            )}
          </div>
        </>
      )}
      <ConfirmationDialog
        type={ConfirmationDialogTypes.CONFIRM}
        isOpen={showCompleteDialog}
        closeModal={() => setShowCompleteDialog(false)}
        onSubmit={onCompleteOrder}
        title="Are you sure you want to set this order as completed?"
        description="This will close the order and no further changes will be possible."
      />
      <ConfirmationDialog
        type={ConfirmationDialogTypes.CONFIRM}
        isOpen={dueTimeChangeConfirmVisible}
        closeModal={() => setDueTimeChangeConfirmVisible(false)}
        onSubmit={() => onDueTimeChange(selectedDueTimeChange)}
        title={`Confirm order delay by ${selectedDueTimeChange} min?`}
        description="Client will be informed about new delivery time."
      />
      <OrderRefundRequestAddForm
        closeModal={() => setShowRefundDialog(false)}
        isOpen={showRefundDialog}
        orderId={order?._id}
        onSaved={() => {
          findOrders(true);
        }}
        orderPaidAmount={totalFullOrderPaid}
        tipsPaidAmount={totalFullTipsPaid}
        orderRefundedAmount={totalFullOrderRefundRequested}
        tipsRefundedAmount={totalFullTipsRefundRequested}
      />
    </ListLayout>
  );
};
